<template>
  <DrawerModal
    :is-open="isOpen"
    title="spark.proposal.choose_place.title"
    subtitle="spark.proposal.choose_place.subtitle"
    @close="props.onClose?.()"
  >
    <div class="ChosePlace">
      <div class="options">
        <div v-if="followingLocations.length" class="section-header">
          {{ localize('spark.proposal.choose_place.following') }}
        </div>

        <div v-if="followingLocations.length" class="spots-grid">
          <div
            v-for="location in followingLocations"
            :key="`fav-${location.id}`"
            :class="['spot-item', { 'is-selected': chosenLocationId === location.id }]"
            @click="chosenLocationId = location.id"
          >
            <div class="spot-avatar">
              <img v-if="location.coverBlobHash" :src="getImageSrc(location.coverBlobHash, 160)" />
              <GamIcon v-else :name="GamIconName.LOCATION" size="24px" />
            </div>
            <span class="spot-name">{{ location.name }}</span>
          </div>
        </div>

        <div v-if="followingLocations.length && nonFollowingLocations.length" class="section-divider" />
        <div v-if="nonFollowingLocations.length" class="spots-grid">
          <div
            v-for="location in nonFollowingLocations"
            :key="`spot-${location.id}`"
            :class="['spot-item', { 'is-selected': chosenLocationId === location.id }]"
            @click="chosenLocationId = location.id"
          >
            <div class="spot-avatar">
              <img v-if="location.coverBlobHash" :src="getImageSrc(location.coverBlobHash, 160)" />
              <GamIcon v-else :name="GamIconName.LOCATION" size="24px" />
            </div>
            <span class="spot-name">{{ location.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <span v-if="props.onChooseOnMap" class="drawer-cta map-cta" @click="props.onChooseOnMap?.()">
        {{ localize('spark.proposal.choose_on_map') }}
      </span>
      <span class="drawer-cta done-cta" :class="{ 'is-confirm': !!chosenLocationId }" @click="handleConfirm">
        {{ localize('spark.proposal.choose_place.done') }}
      </span>
    </template>
  </DrawerModal>
</template>

<script setup lang="ts">
import type { SegmentLocation } from '@/core/data/spark/spark.interface';
import { gambits, localize } from '@/core/gambits';
import DrawerModal from '@/views/components/DrawerModal.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { computed, ref, watch, type Ref } from 'vue';

interface ChoosePlaceProps {
  isOpen: boolean;
  onConfirm?: (locationId: string) => void;
  onClose?: () => void;
  onChooseOnMap?: () => void;
  locations: Ref<SegmentLocation[]> | SegmentLocation[];
  initialLocation: Ref<SegmentLocation | null> | SegmentLocation | null;
}

const props = defineProps<ChoosePlaceProps>();

// State

const chosenLocationId = ref<string>('');
const locationOptions = computed(() => {
  return Array.isArray(props.locations) ? props.locations : props.locations.value;
});

const followingLocations = computed(() => {
  return locationOptions.value.filter((loc) => loc.isFavorite);
});

const nonFollowingLocations = computed(() => {
  return locationOptions.value.filter((loc) => !loc.isFavorite);
});

const getImageSrc = (blobHash: string, width: number) => {
  return gambits.imageService.getImageSrc(blobHash, width);
};

watch(
  () =>
    (props.initialLocation as Ref<SegmentLocation | null>).value?.id ||
    (props.initialLocation as SegmentLocation | null)?.id,
  (id) => {
    const ids = locationOptions.value.map((location) => location.id);

    if (id && ids.includes(id)) {
      chosenLocationId.value = id;
      return;
    }

    chosenLocationId.value = ids[0] || '';
  },
  { immediate: true },
);

watch(
  () => locationOptions.value.map((location) => location.id),
  (ids) => {
    if (!ids.length) {
      chosenLocationId.value = '';
      return;
    }

    if (!chosenLocationId.value || !ids.includes(chosenLocationId.value)) {
      chosenLocationId.value = ids[0];
    }
  },
  { immediate: true },
);

// Life-cycle

watch(
  () => props.isOpen,
  (open) => {
    if (!open) return;

    setTimeout(() => {
      const selected = document.querySelector('.ChosePlace .is-selected');
      selected?.scrollIntoView({ block: 'nearest' });
    }, 100);
  },
);

// Actions

const handleConfirm = () => {
  if (!chosenLocationId.value) return;

  props.onConfirm?.(chosenLocationId.value);
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.ChosePlace {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1rem;
  width: 100%;
}

/* Two-CTA footer like the walk reveal drawer, each half of the drawer width. */
.drawer-cta {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 3.5rem;
  padding: 0 20px;
  border-radius: 2rem;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms linear;

  @include press;
}

.map-cta {
  color: var(--color-white-70);
  border: 1px solid rgba(255, 255, 255, 0.27);
  background-color: rgba(255, 255, 255, 0.1);

  @include hover {
    background-color: rgba(255, 255, 255, 0.2);
    color: var(--color-white);
  }

  @include press;
}

.done-cta {
  color: var(--color-white-70);
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  /* Mirrors the invite players confirm CTA once a selection exists. */
  &.is-confirm {
    background-color: var(--color-pink);
    border-color: var(--color-pink);
    color: var(--color-white);

    @include hover {
      filter: brightness(1.1);
    }
  }

  @include press;
}

.options {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
}

.section-header {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-white-30);
  padding: 0.25rem 0.25rem 0;
}

.section-divider {
  height: 1px;
  background: var(--color-white-10);
  margin: 0.5rem 1rem;
}

/* Same tile size as the multiplayer boards overlay: 88px cells, 80px avatars. */
.spots-grid {
  display: grid;
  grid-template-columns: repeat(3, 88px);
  justify-content: space-evenly;
  row-gap: var(--spacing-l);
}

.spot-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-s);
  width: 88px;
  cursor: pointer;
  transition: opacity var(--transition);

  @include hover {
    opacity: 0.8;
  }
}

.spot-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-dark-500);
  border: 2px solid var(--color-white-30);
  flex-shrink: 0;
  transition: border-color 150ms ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.spot-item.is-selected .spot-avatar {
  border-color: var(--color-pink);
  box-shadow: 0 0 0 1px var(--color-pink);
}

.spot-name {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: var(--color-white-70);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  max-width: 100%;
  line-height: 1.3;
}

.spot-item.is-selected .spot-name {
  color: var(--color-white);
}
</style>
